import React, { useContext, useEffect, useRef, useState } from "react";
import { StyleSheet, View, Dimensions } from "react-native";
import { Modal, Text, TouchableOpacity } from "react-native";
import { useForm } from "react-hook-form";
import { Feather } from "@expo/vector-icons";
import { DataTable } from "react-native-paper";
import { ScrollView } from "react-native-gesture-handler";
import Spinner from "../../../components/shared/Spinner";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "react-native-toast-notifications";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import ChinaTrackerRejectModal from "./ChinaTrackerRejectModal";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useIsFocused } from "@react-navigation/native";
import {
  approveIncomingOrder,
  updateBulkIncomingOrder,
  getMergedTrackerOrders,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import {
  translateFields,
  translateHeaders,
  useTranslation,
} from "../../../utils/useTranslation";
import { chinaTrackerTranslationMap } from "../../../utils/translations";
import {
  resetIncomingSearch,
  resetRejectedSearch,
  searchIncomingTrackerOrders,
  searchRejectedTrackerOrders,
} from "../../../redux/reducers/chinaTrackerReducer";
import { ChinaTrackerDataTableWithPagination2 } from "../../../components/custom/DataTable/tracker/ChinaTrackerDataTableWithPagination2";

interface TrackerEnquiryModalProps {
  visible: boolean;
  onClose: any;
  order: string;
}

// Define headers for the data table
const tableHeadersData = {
  MSRef: {
    name: "MS No.",
    width: 0.22,
    fontWeight: 700,
  },
  OrderNumber1: {
    name: "Order Number",
    width: 0.22,
    fontWeight: 700,
  },
  ItemCode: {
    name: "Item Code",
    width: 0.22,
  },
  ItemName: {
    name: "Item Name",
    width: 1,
  },
  BPName: {
    name: "Customer",
    width: 1,
  },
  StateDelivery: {
    name: "State",
    width: 0.16,
    fontWeight: 700,
  },
  OrderDate: {
    name: "PO Order Date",
    width: 0.22,
    fontWeight: 700,
  },
  PriorityOrder: {
    name: "Priority Order",
    width: 0.22,
    fontWeight: 700,
  },
  ArtworkNo: {
    name: "Artwork No",
    width: 0.22,
    fontWeight: 700,
  },
  RequestDate: {
    name: "Due Date",
    width: 0.22,
    fontWeight: 700,
  },
  TotalOrdered: {
    name: "Order Qty (UoM)",
    width: 0.2,
    fontWeight: 700,
  },
  OrderStatus: {
    name: "Status",
    width: 0.25,
    fontWeight: 700,
  },
  Key: {
    name: "Key",
    width: 0.2,
    textAlign: "left",
    display: "hidden",
  },
  Checkbox: {
    name: "Checkbox",
    width: 0.05,
    display: "none",
  },
};

const ChinaTrackerIncomingOrders: React.FC<TrackerEnquiryModalProps> = ({
  visible,
  onClose,
  order,
}: any) => {
  const listRef = useRef<ScrollView>(null);
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get("window").width,
  );
  const [screenHeight, setScreenHeight] = useState(
    Dimensions.get("window").height,
  );
  const [tableHeaders, setTableHeaders] =
    useState<Partial<typeof tableHeadersData>>(tableHeadersData);
  const { incomingOrders, rejectedOrders, loading, loadingIncomingAdd } =
    useSelector((state: any) => state.chinaTracker);
  const [orders, setOrders] = useState<any[]>([]);
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState("incoming"); // "incoming" or "pending"
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const dispatch = useDispatch<any>();
  const [actionRows, setActionRows] = useState<any>([]);

  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState<number>(0);

  const toast = useToast();
  const { isLargeDevice, isMediumDevice, isSmallDevice } = useContext(
    DeviceOrientationContext,
  );
  const [, setContentOffSet] = useState<number>(0);

  const [modalOpen, setModalOpen] = useState<boolean>();
  const onClose2 = () => {
    setModalOpen(false);
  };
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedEnquiryType, setSelectedEnquiryType] = useState<any>(null);

  // Column visibility settings
  const [columnSettingsOpen, setColumnSettingsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [settingsBtnPos, setSettingsBtnPos] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const settingsBtnRef = useRef<any>(null);

  // Load saved hidden columns
  useEffect(() => {
    AsyncStorage.getItem("chinatracker_incoming_hidden_columns").then(
      (stored) => {
        if (stored) {
          try {
            setHiddenColumns(JSON.parse(stored));
          } catch {}
        }
      },
    );
  }, []);

  const fromPage = (
    page: number,
    itemPerPage: number,
    rows: any[] | undefined,
  ) => {
    return page * itemPerPage + itemPerPage > (rows || []).length
      ? (rows || []).length
      : page * itemPerPage + itemPerPage;
  };

  const getAllTrackerHeaders = () => {
    let headers = translateHeaders(
      tableHeadersData,
      t,
      chinaTrackerTranslationMap,
    );

    if (activeTab === "incoming") {
      const { Status, ...filteredHeaders } = headers as any;
      return filteredHeaders;
    } else {
      return headers;
    }
  };

  const getFilteredHeaders = (baseHeaders: Record<string, any>) => {
    const visibleEntries = Object.entries(baseHeaders).filter(
      ([key]) => !hiddenColumns.includes(key),
    );
    const totalOriginalFlex = Object.values(baseHeaders).reduce(
      (sum, h) => sum + (Number(h.width) || 0),
      0,
    );
    const totalVisibleFlex = visibleEntries.reduce(
      (sum, [, h]) => sum + (Number(h.width) || 0),
      0,
    );
    const scale =
      totalVisibleFlex > 0 ? totalOriginalFlex / totalVisibleFlex : 1;

    const result: Record<string, any> = {};
    visibleEntries.forEach(([key, value]) => {
      result[key] = { ...value, width: Number(value.width) * scale };
    });
    return result;
  };

  const toggleColumn = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const updated = prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey];
      AsyncStorage.setItem(
        "chinatracker_incoming_hidden_columns",
        JSON.stringify(updated),
      );
      return updated;
    });
  };

  // Date Filters
  const [dateFilter, setDateFilter] = useState<string>("upcomingMonth");

  const parseDate = (dateString: string) => {
    if (!dateString) return null;
    const trimmed = dateString.trim();
    const [day, month, year] = trimmed.split("/");
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  const normalizeStartOfDay = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

  const normalizeEndOfDay = (date: Date) =>
    new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      999,
    );

  const applyDateFilter = (data: any[]) => {
    if (dateFilter === "all") return data;

    const today = new Date();
    const nextMonthStart = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      1,
    );

    let startDate = new Date();
    let endDate = new Date();

    switch (dateFilter) {
      case "nextMonth":
        startDate = nextMonthStart;
        endDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);
        break;

      case "next3Months":
        startDate = nextMonthStart;
        endDate = new Date(today.getFullYear(), today.getMonth() + 4, 0);
        break;

      case "next6Months":
        startDate = nextMonthStart;
        endDate = new Date(today.getFullYear(), today.getMonth() + 7, 0);
        break;

      case "upcomingMonth":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
    }

    startDate = normalizeStartOfDay(startDate);
    endDate = normalizeEndOfDay(endDate);

    return data.filter((o) => {
      const date = parseDate(o.RequestDate);
      return date && date >= startDate && date <= endDate;
    });
  };

  const navigateToOrder = (order: any, action: string) => {
    if (action === "accept") {
      const orders: any = [];
      for (const orderKey of order) {
        if (!orderKey.MSRef) {
          toast.show("Missing MSRef", {
            type: "danger",
          });
        }
        orders.push({
          key: orderKey.Key,
          orderNumber: orderKey.OrderNumber1,
          status: "APPROVED",
          MSRef: orderKey.MSRef.toString(),
        });
      }
      const payload: any = {
        orders: orders,
      };
      dispatch(approveIncomingOrder(payload))
        .unwrap()
        .then(() => {
          toast.show("Incoming order approved successfully", {
            type: "success",
          });
          setActionRows([]);
        })
        .catch((error: any) => {
          toast.show(
            error || "Error occurred during approval of incoming order",
            {
              type: "danger",
            },
          );
        });
    } else if (action === "update_accept") {
      const orders: any = [];
      for (const orderKey of order) {
        if (!orderKey.MSRef) {
          toast.show("Missing MSRef", {
            type: "danger",
          });
        }
        orders.push({
          key: orderKey.Key,
          orderNumber: orderKey.OrderNumber1,
          status: "APPROVED",
          MSRef: orderKey.MSRef.toString(),
        });
      }
      const payload: any = {
        orders: orders,
      };
      dispatch(updateBulkIncomingOrder(payload))
        .unwrap()
        .then(() => {
          toast.show("Incoming order updated as approved successfully", {
            type: "success",
          });
          setActionRows([]);
        })
        .catch((error: any) => {
          toast.show(
            error || "Error occurred during approval of incoming order",
            {
              type: "danger",
            },
          );
        });
    } else {
      setModalOpen(true);
      setSelectedItem(order[0]);
      setSelectedEnquiryType(action);
    }
  };
  // Update dimensions when screen size changes
  useEffect(() => {
    const updateDimensions = () => {
      const { width, height } = Dimensions.get("window");
      setScreenWidth(width);
      setScreenHeight(height);
    };

    const dimensionsListener = Dimensions.addEventListener(
      "change",
      updateDimensions,
    );

    return () => {
      dimensionsListener.remove();
    };
  }, []);

  const handleScroll = (event: any) => {
    setContentOffSet(Math.floor(event.nativeEvent?.contentOffset?.y) ?? 0);
  };

  // Calculate modal width based on screen size
  const getModalWidth = () => {
    if (isSmallDevice) return "100%";
    if (isMediumDevice) return "94%";
    return "92%";
  };

  // Calculate inner container width based on screen size
  const getInnerContainerWidth = () => {
    if (isSmallDevice) return "100%";
    return "100%";
  };

  // Hook form setup
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const handleSearch = (searchedText: string) => {
    setPage(0);
    if (searchedText) {
      if (activeTab === "incoming") {
        dispatch(searchIncomingTrackerOrders(searchedText));
      } else {
        dispatch(searchRejectedTrackerOrders(searchedText));
      }
    } else {
      if (activeTab === "incoming") {
        dispatch(resetIncomingSearch());
      } else {
        dispatch(resetRejectedSearch());
      }
    }
  };
  const isFocused = useIsFocused();

  useEffect(() => {
    let isMounted: boolean = true;
    if (isRefresh) {
      dispatch(getMergedTrackerOrders({ refresh: true, showLoading: false }));
      setRefresh(false);
    }

    return () => {
      isMounted = false;
    };
  }, [isRefresh, isLargeDevice, isMediumDevice, isSmallDevice, screenWidth]);

  useEffect(() => {
    setRefresh(false);
    let filtered = activeTab === "pending" ? rejectedOrders : incomingOrders;
    filtered = applyDateFilter(filtered);
    setOrders(filtered);

    let headers = translateHeaders(
      tableHeadersData,
      t,
      chinaTrackerTranslationMap,
    );

    if (activeTab === "incoming") {
      const { Status, ...filteredHeaders } = headers as any;
      setTableHeaders(getFilteredHeaders(filteredHeaders));
    } else {
      setTableHeaders(getFilteredHeaders(headers));
    }
  }, [activeTab, rejectedOrders, incomingOrders, dateFilter, hiddenColumns]);

  return (
    <>
      {modalOpen &&
        ["reject", "update_reject"].includes(selectedEnquiryType) && (
          <ChinaTrackerRejectModal
            onClose={onClose2}
            visible={modalOpen}
            order={selectedItem}
            selectedEnquiryType={selectedEnquiryType}
          />
        )}
      <Modal visible={visible} transparent animationType="slide">
        <View style={styles.overlay}>
          {isLargeDevice ? (
            <View style={[styles.modalContainer, { width: getModalWidth() }]}>
              <View
                style={[
                  styles.modalContainerSecond,
                  {
                    width: getInnerContainerWidth() as any,
                    height: "95%" as any,
                  },
                  isSmallDevice && styles.modalContainerMobile,
                ]}
              >
                <View
                  style={[
                    styles.titleContainer,
                    isSmallDevice && styles.titleContainerMobile,
                  ]}
                >
                  <Text
                    style={[
                      styles.titleText,
                      isSmallDevice && styles.titleTextSmall,
                    ]}
                  >
                    {t("incomingOrder")}
                  </Text>
                  {!isSmallDevice && (
                    <View style={[styles.searchContainer]}>
                      <LabelBannerWithSearchBar
                        titleName={``}
                        handleSearch={handleSearch}
                        setRefresh={setRefresh}
                        dataToExport={orders}
                        fileName="ItemTrackerScreen"
                        sheetName="Item Tracker Report Screen"
                        extraControls={
                          <TouchableOpacity
                            ref={settingsBtnRef}
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: 10,
                              backgroundColor: "#F3F4F6",
                              justifyContent: "center",
                              alignItems: "center",
                              borderWidth: 1,
                              borderColor: columnSettingsOpen
                                ? "#818CF8"
                                : "#E2E8F0",
                            }}
                            onPress={() => {
                              if (settingsBtnRef.current) {
                                settingsBtnRef.current.measureInWindow(
                                  (
                                    x: number,
                                    y: number,
                                    width: number,
                                    height: number,
                                  ) => {
                                    setSettingsBtnPos({
                                      x: x || 0,
                                      y: y || 0,
                                      width: width || 0,
                                      height: height || 0,
                                    });
                                    setColumnSettingsOpen(!columnSettingsOpen);
                                  },
                                );
                              } else {
                                setColumnSettingsOpen(!columnSettingsOpen);
                              }
                            }}
                          >
                            <Feather
                              name="settings"
                              size={15}
                              color={columnSettingsOpen ? "#56B7FD" : "#6B7280"}
                            />
                          </TouchableOpacity>
                        }
                      />
                    </View>
                  )}
                </View>

                {isSmallDevice && (
                  <View
                    style={[styles.searchContainerMobile, { marginBottom: 10 }]}
                  >
                    <LabelBannerWithSearchBar
                      titleName={``}
                      handleSearch={handleSearch}
                      setRefresh={setRefresh}
                      dataToExport={orders}
                      fileName="ItemTrackerScreen"
                      sheetName="Item Tracker Report Screen"
                      extraControls={
                        <TouchableOpacity
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 8,
                            backgroundColor: "#F3F4F6",
                            justifyContent: "center",
                            alignItems: "center",
                            borderWidth: 1,
                            borderColor: columnSettingsOpen
                              ? "#818CF8"
                              : "#E2E8F0",
                          }}
                          onPress={() =>
                            setColumnSettingsOpen(!columnSettingsOpen)
                          }
                        >
                          <Feather
                            name="settings"
                            size={14}
                            color={columnSettingsOpen ? "#56B7FD" : "#6B7280"}
                          />
                        </TouchableOpacity>
                      }
                    />
                  </View>
                )}

                <View style={[styles.tabsContainer, { marginBottom: 12 }]}>
                  <TouchableOpacity
                    style={[
                      styles.tab,
                      activeTab === "incoming" && styles.activeTab,
                    ]}
                    onPress={() => setActiveTab("incoming")}
                  >
                    <Text
                      style={[
                        styles.tabText,
                        activeTab === "incoming" && styles.activeTabText,
                      ]}
                    >
                      {t("incomingOrder")}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.tab,
                      activeTab === "pending" && styles.activeTab,
                    ]}
                    onPress={() => setActiveTab("pending")}
                  >
                    <Text
                      style={[
                        styles.tabText,
                        activeTab === "pending" && styles.activeTabText,
                      ]}
                    >
                      {t("pending")}
                    </Text>
                  </TouchableOpacity>
                </View>

                <View
                  style={[{ marginLeft: 15, marginTop: 10, marginBottom: 10 }]}
                >
                  <ScrollView horizontal>
                    <TouchableOpacity onPress={() => setDateFilter("all")}>
                      <Text
                        style={
                          dateFilter === "all"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("all")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => setDateFilter("upcomingMonth")}
                    >
                      <Text
                        style={
                          dateFilter === "upcomingMonth"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("upcomingMonth")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => setDateFilter("nextMonth")}
                    >
                      <Text
                        style={
                          dateFilter === "nextMonth"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("nextMonth")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => setDateFilter("next3Months")}
                    >
                      <Text
                        style={
                          dateFilter === "next3Months"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("threeMonths")}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => setDateFilter("next6Months")}
                    >
                      <Text
                        style={
                          dateFilter === "next6Months"
                            ? styles.selectedFilter
                            : styles.filterLabel
                        }
                      >
                        {t("sixMonths")}
                      </Text>
                    </TouchableOpacity>
                  </ScrollView>
                </View>

                <View style={styles.actionButtonsContainer}>
                  <Text style={{ display: "flex", alignItems: "center" }}>
                    {actionRows?.length}/10{" "}
                    {translateFields(
                      "ItemsSelected",
                      t,
                      chinaTrackerTranslationMap,
                    )}
                  </Text>
                  <TouchableOpacity
                    style={
                      actionRows.length > 0
                        ? styles.actionButton
                        : styles.disabledActionButton
                    }
                    onPress={() => {
                      activeTab === "incoming"
                        ? navigateToOrder(actionRows, "accept")
                        : navigateToOrder(actionRows, "update_accept");
                    }}
                    disabled={actionRows.length > 0 ? false : true}
                  >
                    <Text style={styles.actionButtonText}>
                      {translateFields(
                        "Accept",
                        t,
                        chinaTrackerTranslationMap,
                      )}{" "}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={
                      actionRows.length === 1
                        ? styles.actionButton
                        : styles.disabledActionButton
                    }
                    disabled={actionRows.length === 1 ? false : true}
                    onPress={() => {
                      activeTab === "incoming"
                        ? navigateToOrder(actionRows, "reject")
                        : navigateToOrder(actionRows, "update_reject");
                    }}
                  >
                    <Text style={styles.actionButtonText}>
                      {translateFields(
                        "Reject",
                        t,
                        chinaTrackerTranslationMap,
                      )}{" "}
                    </Text>
                  </TouchableOpacity>
                </View>

                <ScrollView onScroll={handleScroll} ref={listRef}>
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator
                    contentContainerStyle={{
                      minWidth: Math.max(screenWidth, 1200),
                    }}
                  >
                    <View
                      style={[
                        styles.containerContent,
                        { minWidth: Math.max(screenWidth, 1200) },
                      ]}
                    >
                      {loading || loadingIncomingAdd ? (
                        <View>
                          <Spinner
                            size="small"
                            color="#74BBFF"
                            styles={{ flexDirection: "row", margin: "20%" }}
                          />
                        </View>
                      ) : (
                        <View>
                          <ChinaTrackerDataTableWithPagination2
                            headers={tableHeaders}
                            rows={orders}
                            itemsPerPage={10}
                            isDataLoading={isSearched}
                            isDisplayTimeWithDate={true}
                            useModal={true}
                            isDetail={false}
                            isEdit={false}
                            isDelete={false}
                            actionRows={actionRows}
                            setActionRows={setActionRows}
                            itemTracker
                            // Required props
                            setModalVisible={() => {}}
                            handleRowClick={() => {}}
                            setSelected={() => {}}
                            handleDeleteRow={() => {}}
                            t={t}
                            page={page}
                            setPage={setPage}
                            selectable={true}
                            hidePagination={true}
                          />
                        </View>
                      )}
                    </View>
                  </ScrollView>
                </ScrollView>
                <View
                  style={{
                    borderTopWidth: 1,
                    borderTopColor: "#E2E8F0",
                    backgroundColor: "#fff",
                  }}
                >
                  <DataTable.Pagination
                    page={page}
                    numberOfPages={Math.ceil(orders.length / 10)}
                    onPageChange={(p) => setPage(p)}
                    label={`${fromPage(page, 10, orders)} of ${orders.length}`}
                    showFastPaginationControls
                    numberOfItemsPerPage={10}
                    selectPageDropdownLabel={"Rows per page"}
                  />
                </View>
                <View
                  style={[styles.footer, isSmallDevice && styles.footerSmall]}
                >
                  <TouchableOpacity
                    onPress={onClose}
                    style={[
                      styles.closeButton,
                      isSmallDevice && styles.buttonSmall,
                    ]}
                  >
                    <Text style={isSmallDevice && styles.buttonTextSmall}>
                      {t("close")}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          ) : (
            <View
              style={[
                styles.modalContainerSecond,
                { width: "100%" as any, height: "95%" as any },
                isSmallDevice && styles.modalContainerMobile,
              ]}
            >
              <View
                style={[
                  styles.titleContainer,
                  isSmallDevice && styles.titleContainerMobile,
                ]}
              >
                <Text
                  style={[
                    styles.titleText,
                    isSmallDevice && styles.titleTextSmall,
                  ]}
                >
                  {t("incomingOrder")}
                </Text>
                {!isSmallDevice && (
                  <View style={[styles.searchContainer]}>
                    <LabelBannerWithSearchBar
                      titleName={``}
                      handleSearch={handleSearch}
                      setRefresh={setRefresh}
                      dataToExport={orders}
                      fileName="ItemTrackerScreen"
                      sheetName="Item Tracker Report Screen"
                      extraControls={
                        <TouchableOpacity
                          ref={settingsBtnRef}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                            backgroundColor: "#F3F4F6",
                            justifyContent: "center",
                            alignItems: "center",
                            borderWidth: 1,
                            borderColor: columnSettingsOpen
                              ? "#818CF8"
                              : "#E2E8F0",
                          }}
                          onPress={() => {
                            if (settingsBtnRef.current) {
                              settingsBtnRef.current.measureInWindow(
                                (
                                  x: number,
                                  y: number,
                                  width: number,
                                  height: number,
                                ) => {
                                  setSettingsBtnPos({
                                    x: x || 0,
                                    y: y || 0,
                                    width: width || 0,
                                    height: height || 0,
                                  });
                                  setColumnSettingsOpen(!columnSettingsOpen);
                                },
                              );
                            } else {
                              setColumnSettingsOpen(!columnSettingsOpen);
                            }
                          }}
                        >
                          <Feather
                            name="settings"
                            size={15}
                            color={columnSettingsOpen ? "#56B7FD" : "#6B7280"}
                          />
                        </TouchableOpacity>
                      }
                    />
                  </View>
                )}
              </View>

              {isSmallDevice && (
                <View
                  style={[styles.searchContainerMobile, { marginBottom: 10 }]}
                >
                  <LabelBannerWithSearchBar
                    titleName={``}
                    handleSearch={handleSearch}
                    setRefresh={setRefresh}
                    dataToExport={orders}
                    fileName="ItemTrackerScreen"
                    sheetName="Item Tracker Report Screen"
                    extraControls={
                      <TouchableOpacity
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          backgroundColor: "#F3F4F6",
                          justifyContent: "center",
                          alignItems: "center",
                          borderWidth: 1,
                          borderColor: columnSettingsOpen
                            ? "#818CF8"
                            : "#E2E8F0",
                        }}
                        onPress={() =>
                          setColumnSettingsOpen(!columnSettingsOpen)
                        }
                      >
                        <Feather
                          name="settings"
                          size={14}
                          color={columnSettingsOpen ? "#56B7FD" : "#6B7280"}
                        />
                      </TouchableOpacity>
                    }
                  />
                </View>
              )}

              <View style={[styles.tabsContainer, { marginBottom: 12 }]}>
                <TouchableOpacity
                  style={[
                    styles.tab,
                    activeTab === "incoming" && styles.activeTab,
                  ]}
                  onPress={() => setActiveTab("incoming")}
                >
                  <Text
                    style={[
                      styles.tabText,
                      activeTab === "incoming" && styles.activeTabText,
                    ]}
                  >
                    {t("incomingOrder")}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.tab,
                    activeTab === "pending" && styles.activeTab,
                  ]}
                  onPress={() => setActiveTab("pending")}
                >
                  <Text
                    style={[
                      styles.tabText,
                      activeTab === "pending" && styles.activeTabText,
                    ]}
                  >
                    {t("pending")}
                  </Text>
                </TouchableOpacity>
              </View>

              <View
                style={[
                  styles.actionButtonsContainer,
                  { marginLeft: 15, marginTop: 10, marginBottom: 10 },
                ]}
              >
                <TouchableOpacity
                  style={
                    actionRows.length > 0
                      ? styles.actionButton
                      : styles.disabledActionButton
                  }
                  onPress={() => {
                    activeTab === "incoming"
                      ? navigateToOrder(actionRows, "accept")
                      : navigateToOrder(actionRows, "update_accept");
                  }}
                  disabled={actionRows.length > 0 ? false : true}
                >
                  <Text style={styles.actionButtonText}>
                    {translateFields("Accept", t, chinaTrackerTranslationMap)}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={
                    actionRows.length > 0
                      ? styles.actionButton
                      : styles.disabledActionButton
                  }
                  disabled={actionRows.length > 0 ? false : true}
                  onPress={() => {
                    activeTab === "incoming"
                      ? navigateToOrder(actionRows, "reject")
                      : navigateToOrder(actionRows, "update_reject");
                  }}
                >
                  <Text style={styles.actionButtonText}>
                    {translateFields("Reject", t, chinaTrackerTranslationMap)}
                  </Text>
                </TouchableOpacity>
              </View>

              <ScrollView
                onScroll={handleScroll}
                ref={listRef}
                style={isSmallDevice && styles.scrollBodySectionMobile}
              >
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator
                  contentContainerStyle={{
                    minWidth: Math.max(screenWidth, 1200),
                  }}
                >
                  <View
                    style={[
                      styles.containerContent,
                      { minWidth: Math.max(screenWidth, 1200) },
                    ]}
                  >
                    {loading || loadingIncomingAdd ? (
                      <View>
                        <Spinner
                          size="small"
                          color="#74BBFF"
                          styles={{ flexDirection: "row", margin: "20%" }}
                        />
                      </View>
                    ) : (
                      <View>
                        <ChinaTrackerDataTableWithPagination2
                          headers={tableHeaders}
                          rows={orders}
                          itemsPerPage={10}
                          isDataLoading={isSearched}
                          isDisplayTimeWithDate={true}
                          useModal={true}
                          isDetail={false}
                          isEdit={false}
                          isDelete={false}
                          actionRows={actionRows}
                          setActionRows={setActionRows}
                          itemTracker
                          // Required props
                          setModalVisible={() => {}}
                          handleRowClick={() => {}}
                          setSelected={() => {}}
                          handleDeleteRow={() => {}}
                          page={page}
                          setPage={setPage}
                          selectable={true}
                          hidePagination={true}
                        />
                      </View>
                    )}
                  </View>
                </ScrollView>
              </ScrollView>
              <View
                style={{
                  borderTopWidth: 1,
                  borderTopColor: "#E2E8F0",
                  backgroundColor: "#fff",
                }}
              >
                <DataTable.Pagination
                  page={page}
                  numberOfPages={Math.ceil(orders.length / 10)}
                  onPageChange={(p) => setPage(p)}
                  label={`${fromPage(page, 10, orders)} of ${orders.length}`}
                  showFastPaginationControls
                  numberOfItemsPerPage={10}
                  selectPageDropdownLabel={"Rows per page"}
                />
              </View>
              <View
                style={[styles.footer, isSmallDevice && styles.footerSmall]}
              >
                <TouchableOpacity
                  onPress={onClose}
                  style={[
                    styles.closeButton,
                    isSmallDevice && styles.buttonSmall,
                  ]}
                >
                  <Text style={isSmallDevice && styles.buttonTextSmall}>
                    Close
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </Modal>

      {/* Settings Modal Layer */}
      {columnSettingsOpen && (
        <Modal
          visible={columnSettingsOpen}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setColumnSettingsOpen(false)}
        >
          <TouchableOpacity
            style={{
              flex: 1,
              backgroundColor: "transparent",
            }}
            activeOpacity={1}
            onPress={() => setColumnSettingsOpen(false)}
          >
            <TouchableOpacity
              activeOpacity={1}
              style={{
                position: "absolute",
                top: isSmallDevice
                  ? "50%"
                  : settingsBtnPos.y + settingsBtnPos.height + 6,
                left: isSmallDevice ? "50%" : settingsBtnPos.x - 180 + 40,
                transform: isSmallDevice
                  ? [{ translateX: -110 }, { translateY: -150 }]
                  : [],
                backgroundColor: "#fff",
                borderRadius: 12,
                borderWidth: 1,
                borderColor: "#E2E8F0",
                paddingVertical: 10,
                minWidth: 220,
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 8 },
                shadowOpacity: 0.15,
                shadowRadius: 20,
                elevation: 10,
                zIndex: 9999,
              }}
              onPress={(e) => e.stopPropagation()}
            >
              <Text
                style={{
                  fontSize: 12,
                  fontWeight: "700",
                  color: "#6B7280",
                  paddingHorizontal: 16,
                  paddingBottom: 8,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Show / Hide Columns
              </Text>
              <View
                style={{
                  height: 1,
                  backgroundColor: "#F1F5F9",
                  marginBottom: 4,
                }}
              />
              {/* Select All */}
              <TouchableOpacity
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  paddingVertical: 8,
                  paddingHorizontal: 16,
                }}
                onPress={() => {
                  const allKeys = Object.keys(getAllTrackerHeaders()).filter(
                    (k) => k !== "",
                  );
                  if (hiddenColumns.length === 0) {
                    const updated = [...allKeys];
                    setHiddenColumns(updated);
                    AsyncStorage.setItem(
                      "chinatracker_incoming_hidden_columns",
                      JSON.stringify(updated),
                    );
                  } else {
                    setHiddenColumns([]);
                    AsyncStorage.setItem(
                      "chinatracker_incoming_hidden_columns",
                      JSON.stringify([]),
                    );
                  }
                }}
              >
                <View
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    borderWidth: 1.5,
                    borderColor:
                      hiddenColumns.length === 0 ? "#56B7FD" : "#D1D5DB",
                    backgroundColor:
                      hiddenColumns.length === 0 ? "#56B7FD" : "#fff",
                    justifyContent: "center",
                    alignItems: "center",
                    marginRight: 10,
                  }}
                >
                  {hiddenColumns.length === 0 && (
                    <Feather name="check" size={13} color="#fff" />
                  )}
                </View>
                <Text
                  style={{
                    fontSize: 14,
                    color: "#111827",
                    fontWeight: "700",
                  }}
                >
                  Select All
                </Text>
              </TouchableOpacity>
              <View
                style={{
                  height: 1,
                  backgroundColor: "#F1F5F9",
                  marginBottom: 2,
                }}
              />
              <ScrollView
                style={{ maxHeight: 300 }}
                showsVerticalScrollIndicator={true}
              >
                {Object.entries(getAllTrackerHeaders()).map(
                  ([key, value]: any) => {
                    if (
                      key === "" ||
                      (value as any).display === "none" ||
                      (value as any).display === "hidden"
                    )
                      return null;
                    const isVisible = !hiddenColumns.includes(key);
                    return (
                      <TouchableOpacity
                        key={key}
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          paddingVertical: 8,
                          paddingHorizontal: 16,
                        }}
                        onPress={() => toggleColumn(key)}
                      >
                        <View
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 4,
                            borderWidth: 1.5,
                            borderColor: isVisible ? "#56B7FD" : "#D1D5DB",
                            backgroundColor: isVisible ? "#56B7FD" : "#fff",
                            justifyContent: "center",
                            alignItems: "center",
                            marginRight: 10,
                          }}
                        >
                          {isVisible && (
                            <Feather name="check" size={13} color="#fff" />
                          )}
                        </View>
                        <Text
                          style={{
                            fontSize: 14,
                            color: "#374151",
                            flex: 1,
                          }}
                        >
                          {String(value.name || key)}
                        </Text>
                      </TouchableOpacity>
                    );
                  },
                )}
              </ScrollView>
            </TouchableOpacity>
          </TouchableOpacity>
        </Modal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.15)",
    paddingVertical: 16,
    paddingHorizontal: 8,
  },
  modalContainer: {
    backgroundColor: "#fff",
    padding: 20,
    elevation: 12,
    borderRadius: 12,
    width: "100%",
    maxHeight: "95%",
  },
  modalContainerSecond: {
    backgroundColor: "#fff",
    elevation: 12,
    borderRadius: 12,
    padding: 16,
    width: "100%",
    height: "95%",
  },
  titleContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
    paddingVertical: 12,
    marginBottom: 12,
  },
  titleText: {
    fontSize: 18,
    fontWeight: "700",
  },
  titleTextSmall: {
    fontSize: 14,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  searchInput: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 4,
    padding: 8,
    width: 200,
  },
  searchButton: {
    backgroundColor: "#f0f0f0",
    padding: 8,
    borderRadius: 4,
    marginLeft: 5,
  },
  tabsContainer: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    marginBottom: 8,
  },
  tab: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  activeTab: {
    borderBottomColor: "#3498db",
  },
  tabText: {
    color: "#999",
  },
  activeTabText: {
    color: "#3498db",
    fontWeight: "600",
  },
  actionButtonsContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 12,
    gap: 10,
  },
  actionButton: {
    backgroundColor: "#56B7FD",
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 5,
    marginLeft: 10,
    width: 67,
  },
  disabledActionButton: {
    backgroundColor: "#D8D8D8",
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 5,
    marginLeft: 10,
    width: 67,
  },
  actionButtonText: {
    fontSize: 10,
    color: "#fff",
    fontWeight: 700,
  },
  formSection: {
    marginTop: 20,
    paddingHorizontal: 10,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginTop: 20,
    padding: 25,
  },
  footerSmall: {
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  closeButton: {
    padding: 10,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    width: 120,
    alignItems: "center",
  },
  buttonSmall: {
    padding: 8,
    paddingHorizontal: 12,
  },
  buttonTextSmall: {
    fontSize: 12,
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
    width: "100%",
    paddingHorizontal: 0,
    paddingBottom: 0,
  },

  tableScrollWrapper: {
    flex: 1,
    maxHeight: 520,
    width: "100%",
  },

  filterLabel: {
    fontSize: 12,
    fontWeight: "500",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginHorizontal: 4,
    backgroundColor: "#F1F5F9",
    color: "#4B5563",
    overflow: "hidden",
  },

  selectedFilter: {
    fontSize: 12,
    fontWeight: "600",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginHorizontal: 4,
    backgroundColor: "#56B7FD",
    color: "#fff",
    overflow: "hidden",
  },
  scrollBodySectionMobile: {
    paddingTop: 4,
  },
  modalContainerMobile: {
    padding: 10,
    borderRadius: 10,
  },
  titleContainerMobile: {
    flexDirection: "column",
    alignItems: "flex-start",
    paddingVertical: 8,
    marginBottom: 4,
    gap: 6,
  },
  searchContainerMobile: {
    width: "100%",
  },
});

export default ChinaTrackerIncomingOrders;
